import * as React from "react";
import { Card, CardBody, CardImage } from "@progress/kendo-react-layout";
import { Form, Field, FormElement, FormRenderProps } from "@progress/kendo-react-form";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import { useInjection } from "@presentation/components/dependencyInjection";
import { useNotifications } from "@presentation/components/notifications";
import localStyles from "@presentation/components/loginCard/LoginCard.module.scss";
import { useState, useRef, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { API_ENDPOINTS } from "@infrastructure/InfrastructureConstants";
import { userIcon } from "@progress/kendo-svg-icons";
import { signInProgress } from "@presentation/components/helpers/constants";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { UserCredentials } from "@domain/schemas/BI";
import { AuthService } from "@infrastructure/services";

/**
 * Component with the login feature, accepting username and password credentials to create a new session.
 * @returns The LoginCard component.
 */
const LoginCard: React.FC = () => {
  const authService = useInjection(AuthService);
  const { pushNotification } = useNotifications();
  const { t: loc } = useTranslation();
  const [loginFailedMessage, setLoginFailedMessage] = useState("");
  const submitButtonRef = useRef<Button>(null);

  // Focus the submit button when the component mounts
  useLayoutEffect(() => {
    if (submitButtonRef.current) {
      submitButtonRef.current.element?.focus();
    }
  }, []);

  /**
   * Handles the form submission for user login.
   * @param values - The form values containing username and password.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmit = async (values: { [name: string]: any }) => {
    const userCredentials: UserCredentials = {
      username: values.username,
      password: values.password,
    };
    authService
      .signInWithCredentials(userCredentials)
      .then(() => {
        sessionStorage.removeItem(signInProgress);
        console.info("Credentials login successful");
        window.location.href = "/";
      })
      .catch((error) => {
        sessionStorage.removeItem(signInProgress);
        const message = error?.response?.data?.detail || error?.response?.data?.title || error.message || "Login failed.";
        pushNotification(message, "error", 5000);
        setLoginFailedMessage(message);
      });
  };

  /**
   * Handles the OIDC login by redirecting the user to the OIDC endpoint.
   */
  const handleOidcLogin = () => {
    sessionStorage.setItem(signInProgress, "true");
    const redirectUri = `${window.location.origin}/auth/callback`;
    window.location.href = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.oidc}?redirect_uri=${encodeURIComponent(redirectUri)}`;
  };

  return (
    <div className={localStyles.centered}>
      <Card 
        className={localStyles.card_container} 
        style={{ 
          width: '600px', 
          minHeight: '600px', 
          padding: '30px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center' 
        }}
      >
        <CardBody>
          <div className={localStyles.centered}>
            <CardImage 
              src="/omicsFlowLogo_RGB2_16x16.svg" 
              className={localStyles.medium_logo} 
              alt="logo" 
              style={{ width: '250px', height: 'auto' }} // Adjusted logo size for the larger card
            />
          </div>
          <br />
          <div className={localStyles.centered}>
            <Form
              onSubmit={handleSubmit}
              initialValues={{ username: "", password: "" }}
              render={(formRenderProps: FormRenderProps) => (
                <FormElement>
                  <Field id={"username"} name={"username"} component={Input} label={loc("username")} />
                  <Field id={"password"} name={"password"} type={"password"} label={loc("password")} component={Input} />
                  {loginFailedMessage && <div className={`${localStyles.error_message} ${localStyles.centered}`}>{loginFailedMessage}</div>}
                  <div className={`${localStyles.padding_top}`}>
                    <Button
                      type={"submit"}
                      disabled={!formRenderProps.allowSubmit || !formRenderProps.valueGetter("username")?.trim() || !formRenderProps.valueGetter("password")?.trim()}
                      themeColor={"primary"}
                      ref={submitButtonRef}
                    >
                      {loc("submit")}
                    </Button>
                  </div>
                </FormElement>
              )}
            />
          </div>
          <div className={`${localStyles.centered} ${localStyles.padding_top}`}>
            <Button 
              svgIcon={userIcon} 
              className={localStyles.oidc_login_button} 
              type="button" 
              themeColor="primary" 
              onClick={handleOidcLogin}
            >
              <span className={localStyles.redirect_arrow}>&#10532;</span>
              {loc("oidcSignIn")}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default LoginCard;